// ======================================================
// FILE: src/pages/ManagerLeaveReview.jsx
// ======================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  getAllLeaves,
  updateLeaveStatus,
} from "../../api/api";

const ManagerLeaveReview = () => {

  // ======================================================
  // STATES
  // ======================================================

  const [leaves, setLeaves] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [processingId, setProcessingId] =
    useState(null);

  const [remarks, setRemarks] =
    useState({});

  const [activeTab, setActiveTab] =
    useState("pending");

  // ======================================================
  // FETCH LEAVES
  // ======================================================

  const fetchLeaves = async () => {

    try {

      setLoading(true);

      const response =
        await getAllLeaves();

      console.log(
        "Leaves Response:",
        response
      );

      setLeaves(
        Array.isArray(response)
          ? response
          : []
      );

    } catch (error) {

      console.error(
        "Error fetching leaves:",
        error
      );

      alert(
        "Failed to load leave requests"
      );

    } finally {

      setLoading(false);
    }
  };

  useEffect(() => {

    fetchLeaves();

  }, []);

const formatDate = (date) => {

  if (!date) return "-";

  return new Date(date)
    .toLocaleDateString(
      "en-GB"
    );
};
  // ======================================================
  // HANDLE REMARK CHANGE
  // ======================================================

  const handleRemarkChange = (
    id,
    value
  ) => {

    setRemarks((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // ======================================================
  // APPROVE
  // ======================================================

  const handleApprove = async (
    leaveId
  ) => {

    try {

      setProcessingId(leaveId);

      await updateLeaveStatus(
        leaveId,
        "approved",
        remarks[leaveId] || ""
      );

      alert(
        "Leave approved successfully"
      );

      fetchLeaves();

    } catch (error) {

      console.error(error);

      alert(
        error?.error ||
        "Failed to approve leave"
      );

    } finally {

      setProcessingId(null);
    }
  };

  // ======================================================
  // REJECT
  // ======================================================

  const handleReject = async (
    leaveId
  ) => {

    const remark =
      remarks[leaveId];

    if (
      !remark ||
      !remark.trim()
    ) {

      alert(
        "Remark is required to reject leave"
      );

      return;
    }

    const confirmReject =
      window.confirm(
        "Are you sure you want to reject this leave?"
      );

    if (!confirmReject) return;

    try {

      setProcessingId(leaveId);

      await updateLeaveStatus(
        leaveId,
        "rejected",
        remark
      );

      alert(
        "Leave rejected successfully"
      );

      fetchLeaves();

    } catch (error) {

      console.error(error);

      alert(
        error?.error ||
        "Failed to reject leave"
      );

    } finally {

      setProcessingId(null);
    }
  };

  // ======================================================
  // FILTERED LEAVES
  // ======================================================

  const filteredLeaves =
    leaves.filter((leave) => {

      if (activeTab === "all") {
        return true;
      }

      return (
        leave.status?.toLowerCase() ===
        activeTab
      );
    });

  // ======================================================
  // LOADING
  // ======================================================

  if (loading) {

    return (

      <div
        style={{
          padding: "20px",
          textAlign: "center",
          fontSize: "16px",
        }}
      >
        Loading leave requests...
      </div>
    );
  }

  // ======================================================
  // UI
  // ======================================================

  return (

    <div
      style={{
        padding: "16px",
        background: "#f4f6f9",
        minHeight: "100vh",
      }}
    >

      {/* ====================================================== */}
      {/* HEADER */}
      {/* ====================================================== */}

      <div
        style={{
          marginBottom: "20px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "24px",
            color: "#1f2937",
          }}
        >
          Leave Review
        </h2>

        <p
          style={{
            marginTop: "5px",
            color: "#6b7280",
          }}
        >
          Review and manage employee leave requests
        </p>
      </div>

      {/* ====================================================== */}
      {/* TABS */}
      {/* ====================================================== */}

      <div
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "20px",
          overflowX: "auto",
        }}
      >

        {[
          "pending",
          "approved",
          "rejected",
          "all",
        ].map((tab) => (

          <button
            key={tab}
            onClick={() =>
              setActiveTab(tab)
            }
            style={{
              padding:
                "10px 18px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              background:
                activeTab === tab
                  ? "#2563eb"
                  : "#e5e7eb",
              color:
                activeTab === tab
                  ? "#fff"
                  : "#111827",
              fontWeight: 600,
              textTransform:
                "capitalize",
              minWidth: "100px",
              whiteSpace: "nowrap",
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ====================================================== */}
      {/* EMPTY */}
      {/* ====================================================== */}

      {filteredLeaves.length === 0 ? (

        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: "#fff",
            borderRadius: "12px",
          }}
        >
          No leave requests found
        </div>

      ) : (

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >

          {filteredLeaves.map(
            (leave) => (

              <div
                key={leave.id}
                style={{
                  background: "#fff",
                  borderRadius:
                    "14px",
                  padding: "18px",
                  boxShadow:
                    "0 2px 8px rgba(0,0,0,0.08)",
                }}
              >

                {/* ====================================================== */}
                {/* TOP */}
                {/* ====================================================== */}

                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems:
                      "flex-start",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >

                  <div>

                    <h3
                      style={{
                        margin: 0,
                        color:
                          "#111827",
                        fontSize:
                          "18px",
                      }}
                    >
                      {
                        leave.user_name ||
                        leave.employee_name ||
                        "Employee"
                      }
                    </h3>

                    <p
                      style={{
                        margin:
                          "6px 0",
                        color:
                          "#6b7280",
                      }}
                    >
                      {
                        leave.leave_type_name ||
                        leave.leave_type?.name ||
                        "Leave"
                      }
                    </p>
                  </div>

                  <div
                    style={{
                      padding:
                        "6px 12px",
                      borderRadius:
                        "999px",
                      fontSize:
                        "13px",
                      fontWeight: 600,
                      textTransform:
                        "capitalize",
                      background:
                        leave.status ===
                        "approved"
                          ? "#dcfce7"
                          : leave.status ===
                            "rejected"
                          ? "#fee2e2"
                          : "#fef3c7",
                      color:
                        leave.status ===
                        "approved"
                          ? "#166534"
                          : leave.status ===
                            "rejected"
                          ? "#991b1b"
                          : "#92400e",
                    }}
                  >
                    {leave.status || "-"}
                  </div>
                </div>

                {/* ====================================================== */}
                {/* DETAILS */}
                {/* ====================================================== */}

                <div
                  style={{
                    marginTop: "16px",
                    display: "grid",
                    gap: "10px",
                  }}
                >

                  <div>
                    <strong>
                      From:
                    </strong>{" "}
                    {formatDate(leave.from_date)}
                  </div>

                  <div>
                    <strong>
                      To:
                    </strong>{" "}
                    {formatDate(leave.to_date)}
                  </div>

                  <div>
                    <strong>
                      Days:
                    </strong>{" "}
                    {leave.days || "-"}
                  </div>

                  <div>
                    <strong>
                      Reason:
                    </strong>{" "}
                    {
                      typeof leave.reason ===
                      "object"
                        ? JSON.stringify(
                            leave.reason
                          )
                        : leave.reason || "-"
                    }
                  </div>

                  <div>
                    <strong>
                      Contact:
                    </strong>{" "}
                    {leave.contact || "-"}
                  </div>

                  <div>
                    <strong>
                      Leave Split:
                    </strong>{" "}
                    {leave.split_summary ||
                      "-"}
                  </div>

                  {leave.approved_at && (

                    <div>
                      <strong>
                        Approved At:
                      </strong>{" "}
                      {new Date(
  leave.approved_at
).toLocaleString(
  "en-GB",
  {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }
)
.replace(/\//g, "-")
.replace(",", "")}
                    </div>
                  )}

                  {leave.medical_file_url && (

                    <div>
                      <strong>
                        Attachment:
                      </strong>{" "}

                      <a
                        href={
                          leave.medical_file_url
                        }
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color:
                            "#2563eb",
                          wordBreak:
                            "break-word",
                        }}
                      >
                        View File
                      </a>
                    </div>
                  )}
                </div>

                {/* ====================================================== */}
                {/* REMARK INPUT */}
                {/* ====================================================== */}

                {leave.status ===
                  "pending" && (

                  <div
                    style={{
                      marginTop:
                        "18px",
                    }}
                  >

                    <textarea
                      placeholder="Enter comment / rejection remark"
                      value={
                        remarks[
                          leave.id
                        ] || ""
                      }
                      onChange={(e) =>
                        handleRemarkChange(
                          leave.id,
                          e.target.value
                        )
                      }
                      rows={3}
                      style={{
                        width: "100%",
                        borderRadius:
                          "10px",
                        border:
                          "1px solid #d1d5db",
                        padding:
                          "12px",
                        resize:
                          "vertical",
                        outline:
                          "none",
                        fontSize:
                          "14px",
                        boxSizing:
                          "border-box",
                      }}
                    />

                    {/* ====================================================== */}
                    {/* ACTION BUTTONS */}
                    {/* ====================================================== */}

                    <div
                      style={{
                        display:
                          "flex",
                        gap: "12px",
                        marginTop:
                          "14px",
                        flexWrap:
                          "wrap",
                      }}
                    >

                      <button
                        onClick={() =>
                          handleApprove(
                            leave.id
                          )
                        }
                        disabled={
                          processingId ===
                          leave.id
                        }
                        style={{
                          flex: 1,
                          minWidth:
                            "120px",
                          padding:
                            "12px",
                          border:
                            "none",
                          borderRadius:
                            "10px",
                          background:
                            "#16a34a",
                          color:
                            "#fff",
                          fontWeight: 600,
                          cursor:
                            "pointer",
                        }}
                      >
                        {processingId ===
                        leave.id
                          ? "Processing..."
                          : "Approve"}
                      </button>

                      <button
                        onClick={() =>
                          handleReject(
                            leave.id
                          )
                        }
                        disabled={
                          processingId ===
                          leave.id
                        }
                        style={{
                          flex: 1,
                          minWidth:
                            "120px",
                          padding:
                            "12px",
                          border:
                            "none",
                          borderRadius:
                            "10px",
                          background:
                            "#dc2626",
                          color:
                            "#fff",
                          fontWeight: 600,
                          cursor:
                            "pointer",
                        }}
                      >
                        {processingId ===
                        leave.id
                          ? "Processing..."
                          : "Reject"}
                      </button>
                    </div>
                  </div>
                )}

                {/* ====================================================== */}
                {/* MANAGER COMMENT */}
                {/* ====================================================== */}

                {leave.manager_comment && (

                  <div
                    style={{
                      marginTop: "18px",
                      background:
                        "#f9fafb",
                      padding: "12px",
                      borderRadius:
                        "10px",
                    }}
                  >
                    <strong>
                      Manager Remark:
                    </strong>

                    <div
                      style={{
                        marginTop:
                          "5px",
                        color:
                          "#374151",
                        whiteSpace:
                          "pre-wrap",
                      }}
                    >
                      {
                        leave.manager_comment
                      }
                    </div>
                  </div>
                )}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default ManagerLeaveReview;